import { parseLineEquation as parseSharedLineEquation, equivalentLines } from "../../../../utils/lineEquation";
import React from "react";

import { BlockMath, InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

const DIRECTIONS = [-4, -3, -2, 2, 3, 4];

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;
const randomItem = (items) => items[Math.floor(Math.random() * items.length)];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function reduceFraction(num, den) {
  const sign = den < 0 ? -1 : 1;
  const divisor = gcd(num, den);
  return { num: (sign * num) / divisor, den: (sign * den) / divisor };
}

function fractionLatex(num, den = 1) {
  const reduced = reduceFraction(num, den);
  if (reduced.den === 1) return `${reduced.num}`;
  const sign = reduced.num < 0 ? "-" : "";
  return `${sign}\\frac{${Math.abs(reduced.num)}}{${reduced.den}}`;
}

function slopeTermLatex(num, den) {
  const reduced = reduceFraction(num, den);
  if (reduced.den === 1) {
    if (reduced.num === 1) return "x";
    if (reduced.num === -1) return "-x";
    return `${reduced.num}x`;
  }
  return `${fractionLatex(reduced.num, reduced.den)}x`;
}

function slopeLineLatex(slopeNum, slopeDen, interceptNum, interceptDen) {
  const slope = slopeTermLatex(slopeNum, slopeDen);
  const intercept = reduceFraction(interceptNum, interceptDen);
  if (intercept.num === 0) return `y=${slope}`;

  return `y=${slope}${intercept.num > 0 ? "+" : "-"}${fractionLatex(
    Math.abs(intercept.num),
    intercept.den
  )}`;
}



function parseLineEquation(raw) { return parseSharedLineEquation(raw, { implicitZero: true }); }



export function generateTask() {
  const midpointX = randomInt(-3, 3);
  const midpointY = randomInt(-3, 3);
  const dx = randomItem(DIRECTIONS);
  const dy = randomItem(DIRECTIONS);
  const slope = reduceFraction(-dx, dy);
  const lineC = -(dx * midpointX + dy * midpointY);
  const intercept = reduceFraction(-lineC, dy);

  return {
    type: "PerpendicularBisectorTask",
    data: {
      xA: midpointX - dx,
      yA: midpointY - dy,
      xB: midpointX + dx,
      yB: midpointY + dy,
      midpointX,
      midpointY,
      segmentSlopeNum: dy,
      segmentSlopeDen: dx,
      perpendicularSlopeNum: slope.num,
      perpendicularSlopeDen: slope.den,
      interceptNum: intercept.num,
      interceptDen: intercept.den,
      lineA: dx,
      lineB: dy,
      lineC,
    },
  };
}

export function renderTask({ data }) {
  const { xA, yA, xB, yB } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Dane są punkty <InlineMath math={`A=(${xA},${yA})`} /> oraz{" "}
        <InlineMath math={`B=(${xB},${yB})`} />.
      </p>
      <p>
        Wyznacz równanie symetralnej odcinka <InlineMath math="AB" />.
        Odpowiedź możesz podać w postaci kierunkowej albo ogólnej.
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ maxWidth: 520 }}>
      <p>
        Równanie symetralnej (np. <InlineMath math="y=-\frac12x+3" /> lub{" "}
        <InlineMath math="x+2y-6=0" />):
      </p>
      <EquationInput
        value={userAnswer?.line || ""}
        onChange={(line) => onChange({ ...userAnswer, line })}
        disabled={disabled}
        placeholder="np. y=-1/2x+3"
        basis="440px"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = parseLineEquation(userAnswer?.line);
  return parsed
    ? equivalentLines(parsed, {
        A: data.lineA,
        B: data.lineB,
        C: data.lineC,
      })
    : false;
}

export function renderAnswer({ data }) {
  return (
    <BlockMath
      math={slopeLineLatex(
        data.perpendicularSlopeNum,
        data.perpendicularSlopeDen,
        data.interceptNum,
        data.interceptDen
      )}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  const answer = userAnswer?.line?.trim();
  return answer ? <InlineMath math={answer} /> : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ data }) {
  const segmentSlope = fractionLatex(
    data.segmentSlopeNum,
    data.segmentSlopeDen
  );
  const perpendicularSlope = fractionLatex(
    data.perpendicularSlopeNum,
    data.perpendicularSlopeDen
  );
  const finalEquation = slopeLineLatex(
    data.perpendicularSlopeNum,
    data.perpendicularSlopeDen,
    data.interceptNum,
    data.interceptDen
  );
  const substitutedX =
    data.midpointX < 0 ? `(${data.midpointX})` : `${data.midpointX}`;
  const bound =
    Math.max(
      6,
      Math.abs(data.xA),
      Math.abs(data.yA),
      Math.abs(data.xB),
      Math.abs(data.yB),
      Math.abs(data.midpointX),
      Math.abs(data.midpointY)
    ) + 1;
  const symetralnaExpression = `y=(${data.perpendicularSlopeNum}/${data.perpendicularSlopeDen})*x+(${data.interceptNum}/${data.interceptDen})`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Obliczamy środek odcinka <InlineMath math="AB" />:
        <BlockMath
          math={`S=\\left(\\frac{${data.xA}+${data.xB}}2,\\frac{${data.yA}+${data.yB}}2\\right)=(${data.midpointX},${data.midpointY})`}
        />
      </div>
      <div>
        <strong>Krok 2.</strong> Obliczamy współczynnik kierunkowy prostej{" "}
        <InlineMath math="AB" />:
        <BlockMath
          math={`a_{AB}=\\frac{${data.yB}-(${data.yA})}{${data.xB}-(${data.xA})}=${segmentSlope}`}
        />
      </div>
      <div>
        <strong>Krok 3.</strong> Symetralna jest prostopadła do{" "}
        <InlineMath math="AB" />, więc jej współczynnik jest przeciwną
        odwrotnością:
        <BlockMath math={`a_s=-\\frac{1}{a_{AB}}=${perpendicularSlope}`} />
      </div>
      <div>
        <strong>Krok 4.</strong> Symetralna przechodzi przez punkt{" "}
        <InlineMath math={`S=(${data.midpointX},${data.midpointY})`} />.
        Podstawiamy go do równania <InlineMath math="y=a_sx+b" />:
        <BlockMath
          math={`${data.midpointY}=${perpendicularSlope}\\cdot${substitutedX}+b`}
        />
        <BlockMath
          math={`b=${fractionLatex(data.interceptNum, data.interceptDen)}`}
        />
        <BlockMath math={finalEquation} />
      </div>
      <div>
        <strong>Wizualizacja:</strong> punkt <InlineMath math="S" /> jest
        środkiem odcinka <InlineMath math="AB" />, a symetralna przechodzi
        przez <InlineMath math="S" /> i jest prostopadła do{" "}
        <InlineMath math="AB" />.
        <div style={{ display: "flex", justifyContent: "center" }}>
          <GraphViewer
            points={{
              A: { x: data.xA, y: data.yA, label: "A" },
              B: { x: data.xB, y: data.yB, label: "B" },
              S: {
                x: data.midpointX,
                y: data.midpointY,
                label: "S",
                color: "#ff9900",
              },
            }}
            segments={[{ p1: "A", p2: "B", color: "orange" }]}
            equations={[
              {
                expr: symetralnaExpression,
                color: "purple",
                label: "symetralna",
              },
            ]}
            xRange={[-bound, bound]}
            yRange={[-bound, bound]}
            width={500}
            height={500}
          />
        </div>
      </div>
    </div>
  );
}

export const __testables = { parseLineEquation, equivalentLines };
